/*
 * Copyright (c) 2020. Dave van Rijn Development
 */

import {FieldDefinition} from "../util/interfaces";
import {IndexedObject} from '@dvrd/dvr-controls';
import merge from 'lodash/merge';

export const createFieldDef = (expectedType: string, defaultValue?: any, allowNull: boolean = false,
                               allowUndefined: boolean = false,
                               convertToBoolean: boolean = false): FieldDefinition => ({
    expectedType,
    allowUndefined,
    allowNull,
    convertToBoolean,
    defaultValue,
});

export abstract class BaseModel<Props extends IndexedObject<any>, Fields extends IndexedObject<any>> extends Object {
    private readonly fieldFilter: string[];
    protected readonly fields: Fields & IndexedObject<any>;
    protected readonly props: Props;
    private readonly FieldsDefinition: IndexedObject<FieldDefinition>;
    private readonly tempFields: IndexedObject<any>;

    protected constructor(fieldFilter: string[], fieldsDefinition: IndexedObject<FieldDefinition>, fields: Fields,
                          props?: Props) {
        super(props);
        fields = merge({}, fields);
        this.fields = fields;
        this.props = props || {} as Props;
        this.fieldFilter = fieldFilter;
        this.FieldsDefinition = fieldsDefinition;
        this.tempFields = {};
        this.constructFields();
    }

    setField = (key: string, value: any) => {
        if (this.fields.hasOwnProperty(key))
            (this.fields as IndexedObject<any>)[key] = value;
    };

    getField = (key: string): any | null => {
        if (this.fields.hasOwnProperty(key))
            return this.fields[key];
        return null;
    };

    setTempField = (key: string, value: any) => {
        this.tempFields[key] = value;
    };

    getTempField = (key: string): any | null => {
        if (key in this.tempFields) return this.tempFields[key];
        return null;
    };

    protected constructFields = (data = this.props) => {
        const preparedData = this.prepareData(data);
        const keys = Object.keys(this.fields).filter(key => !this.fieldFilter.includes(key));
        for (const key of keys) this.initializeField(key, preparedData[key]);
        if (this.build)
            this.build(data);
    };

    protected abstract build?: (data?: Props) => void;

    private initializeField = (key: string, value: any) => {
        if (this.fields.hasOwnProperty(key) && value !== undefined)
            (this.fields as { [index: string]: any })[key] = value;
    };

    private prepareData = (data: Props) => {
        const prepareKeys = Object.keys(this.FieldsDefinition), preparedData: IndexedObject<any> = {};
        for (const key of prepareKeys) {
            const definition: FieldDefinition = this.FieldsDefinition[key],
                {allowNull, allowUndefined, defaultValue} = definition;
            let value = data[key];
            if ((value === null && !allowNull) || (value === undefined && !allowUndefined)) {
                if (defaultValue) preparedData[key] = defaultValue;
                else preparedData[key] = preparedData[key] = this.determineDefaultValue(definition);
            } else if ((value === null && allowNull) || (value === undefined && (allowUndefined || allowNull))) {
                preparedData[key] = null;
            } else
                preparedData[key] = this.validateExistingValue(value, definition);
        }
        return preparedData;
    };

    private determineDefaultValue = (definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition,
            typeToUse = convertToBoolean ? 'boolean' : expectedType;
        switch (typeToUse) {
            case 'string':
                return '';
            case 'number':
                return 0;
            case 'boolean':
                return false;
            case 'object':
                return {};
            case 'array':
                return new Array<any>();
        }
        return null;
    };

    private validateExistingValue = (value: any, definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition;
        if (convertToBoolean && (value === true || value === false)) return value;
        switch (expectedType) {
            case 'string':
                if (typeof value !== 'string') value = value.toString();
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case 'number':
                if (typeof value !== 'number') value = Number(value);
                if (Number.isNaN(value)) value = 0;
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case 'boolean':
                if (value !== true && value !== false) value = false;
                return value;
            case 'object':
                try {
                    Object.keys(value).length;
                } catch {value = {};}
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case 'array':
                if (!Array.isArray(value)) value = [];
                return this.convertBoolean(value, expectedType, convertToBoolean);
            default:
                return null;
        }
    };

    private convertBoolean = (value: any, expectedType: string, shouldConvert: boolean = false): any => {
        if (!shouldConvert) return value;
        switch (expectedType) {
            case 'string':
                return value === 'true';
            case 'number':
                return value === 1;
            case 'boolean':
                return value;
            case 'object':
                try {
                    return Object.keys(value).length > 0;
                } catch {return false;}
            case 'array':
                try {
                    return value.length > 0;
                } catch {return false;}
        }
        return false;
    }
}